import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { DatasetsEditProps } from '@databox/ember-integrations/lib/components/Datasets/Edit/Edit';
import DatasetsEdit from '@databox/ember-integrations/lib/components/Datasets/Edit/EditEntryPoints';

import type { ReactIntegrations, Service } from 'webapp-types';
import { UserLevel } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { getShadowDomMode } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';

import type Source from 'webapp/models-custom/source';
import { getBillingFeature } from 'webapp/models/helpers/react-model-mapper';

interface Args {
	model: string | null;
}

export default class DataManagerConnectedDatasetsEditComponent extends Component<Args> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('account') declare accountService: Service.Account;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('dataset-events') declare datasetEventsService: Service.DatasetEvents;
	@service('billing') declare billingService: Service.Billing;
	@service('react') declare reactService: Service.React;
	@service('ai-chat') declare aiChatService: Service.AIChat;

	reactElement: ReactIntegrations.Root = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);
	}

	get model(): Args['model'] {
		return this.args.model;
	}

	@action
	setupView(node: HTMLElement) {
		this.reactElement = this.reactService.create(node as HTMLDivElement);
		this.renderDatasetEdit();

		this.eventManagerService.on(AppEvent.ACCESS_TOKEN_REFRESHED, this, this.renderDatasetEdit);
		this.eventManagerService.on(AppEvent.THEME_CHANGED, this, this.renderDatasetEdit);
	}

	@action
	handleDatasetSave(sId: Source['id']) {
		this.infolayerService.ok('Dataset was saved successfully!');

		this.navigationService.transitionToRoute('datamanager.connected.datasets.view', sId);
	}

	renderDatasetEdit() {
		if (this.reactElement && this.model !== null) {
			const { accessToken, userDataToken: dataToken } = this.currentUserService;

			const spaceAccessId = Number(this.model);

			const newProps: DatasetsEditProps = {
				isDarkThemeActive: this.navigationService.theme.isDark,
				accessToken: accessToken!,
				dataToken: dataToken!,
				spaceId: this.accountService.id,
				spaceAccessId,
				extra: {
					userProfile: {
						rootLevel: this.accountService.isAdminButNotInClientSpace
							? UserLevel.ADMIN
							: UserLevel.USER,
						rootSpaceId: this.currentUserService.rootSpaceId ?? -1,
						id: +this.currentUserService.id!,
					},
					isAiAgentHiddenForNonAdmins: this.accountService.aiAgentHiddenForNonAdmins,
				},
				onSave: () => this.handleDatasetSave(`${spaceAccessId}`),
				onViewDatasetDetails: () =>
					this.datasetEventsService.handleViewDataset(`${spaceAccessId}`),
				onViewDataManager: this.datasetEventsService.handleViewDataManager,
				onApiError: this.datasetEventsService.handleApiError,
				onPlanUpgrade: async (featureName: string) => {
					return await this.billingService.handlePlanUpgradeForFeature(
						getBillingFeature(featureName),
					);
				},
				getInvalidatedBillingInfoQuery: (invalidatedBillingInfoQuery: () => void) => {
					this.billingService.invalidateFeatureCache = invalidatedBillingInfoQuery;
				},
			};

			this.reactService.render(
				this.reactElement,
				DatasetsEdit(newProps),
				getShadowDomMode(this.accountService.email),
			);
		}
	}

	@action
	teardownView(): void {
		this.reactService.unmount(this.reactElement);
		this.eventManagerService.off(AppEvent.ACCESS_TOKEN_REFRESHED, this);
		this.eventManagerService.off(AppEvent.THEME_CHANGED, this);
	}
	<template>
		<div {{didInsert this.setupView}} {{willDestroy this.teardownView}} id="datasets-container">
		</div>
	</template>
}
